// src/chat.tsx
import mqtt from 'async-mqtt'
import axios from 'axios'
import dayjs from 'dayjs'
import React, {
  ChangeEvent,
  Component,
  createRef,
  FormEvent,
  KeyboardEvent
} from 'react'
import { v4 as uuid } from 'uuid'

import { ChatUIProps } from 'src'

import { ReactComponent as SendIcon } from './assets/send.svg'
import Header from './components/Header'
import LoadingSpinner from './components/LoadingSpinner'
import RenderMessages from './components/MessageComponents/RenderMessages'
import Recorder from './components/Recorder/Recorder'
import AutoRecorder from './components/Recorder/AutomaticRecorder'

import config, { KEY_CONVERSATION_ID } from './config'
import LocalizationContext from './contexts/Localization/LocalizationContext'

import SpeakerIcon from './assets/SpeakerIcon'
import MuteIcon from './assets/MuteIcon'
import CloseIcon from './assets/CloseIcon'

import {
  EventType,
  Language,
  Message,
  MessageType,
  PayloadMessage,
  PayloadMessageTypes,
  Sender,
  Voice,
  WSConfig,
  WSPayloadType,
  WSPublishPayload
} from './globals'

import {
  blockEvent,
  getRandomizeMessage,
  isOverlayTotallyScrolled,
  isSmallDevice,
  isTotallyScrolledReversed,
  sleep
} from './utils'
import { generateAudioUrl } from './generateAudio'

/** ---------- Inline helpers (no external file needed) ---------- */


function generateWaitMessage(
  locale: string,
  voices: Voice[] | undefined,
  waitIndex: number
): string | null {
  const fallback: Record<string, string[]> = {
    sv_SE: voices?.find((x) => x.locale === 'sv_SE')?.waitPhrases ?? [
      'Ett ögonblick',
      'Var god vänta'
    ],
    nb_NO: voices?.find((x) => x.locale === 'nb_NO')?.waitPhrases ?? [
      'Et øyeblikk',
      'Vennligst vent'
    ],
    en_US: voices?.find((x) => x.locale === 'en_US')?.waitPhrases ?? [
      'A moment please',
      'Please wait'
    ]
  }
  const phrases = fallback[locale] || fallback.sv_SE
  if (!phrases?.length) return null
  const text = phrases[waitIndex % phrases.length]
  return generateAudioUrl(text, locale, voices)
}

/** ---------- Global helpers (kept for compatibility) ---------- */
let talkToChatHandler:
  | ((
      message: string,
      sender: Sender,
      type: MessageType,
      sendViaNetwork?: boolean
    ) => void)
  | null = null
let sendMessageHandler:
  | ((message: string, type: MessageType, requestId?: string) => Promise<void>)
  | null = null

export function sendMessage(
  message: string,
  senderType: Sender,
  type: MessageType
) {
  if (!message) {
    console.error("First property 'message' is required in sendMessage")
    return
  }
  if (!senderType) {
    console.error("Second property 'senderType' is required in sendMessage")
    return
  }

  talkToChatHandler?.(message, senderType, type)
}

export function triggerDialog(dialogId: string) {
  if (!dialogId) {
    console.error("First property 'dialogId' is required in sendHiddenMessage")
    return
  }
  sendMessageHandler?.(dialogId, MessageType.TRIGGER)
}

/** ---------- Local constants ---------- */
const KEY_STORED_OPTIONS = 'KEY_STORED_OPTIONS'
const TEMP_SESSION_ID = 'TEMP_SESSION_ID'
const LANGUAGE_STORAGE_KEY = 'chatui_selected_language'
const MUTED_STORAGE_KEY = 'chat-muted'
const INOVIA_TERMS_ACCEPTED = 'inovia-terms-accepted'

const FOOTER_EXTRA_PX = 40 // height of .ChatFooter (one line + padding)
const CALLBAR_EXTRA_PX = 44 // height of CallBar if visible

type ChannelType = 'CHAT' | 'VOICE'

type ChatProps = ChatUIProps & {
  sessionId: null | string
  setSessionId: (newSessionId: null | string, cb: () => void) => void
  transcriptionUrl: string
  baseHost: string
  languages?: Language[]
  voices?: Voice[]
  termsMessage?: string
  termsFrequency?: 'daily' | 'monthly'
  recorderMode?: 'disabled' | 'manual' | 'auto'
  isExpanded: boolean
  onToggleExpand: () => void
  isInbox?: boolean
  transcriptionDisabled?: boolean
}

type ChatState = {
  currentIndex: number
  error: boolean
  isMessageSending: boolean
  isWaitingForAgent: boolean
  aivaIsTyping: number
  isMuted: boolean
  textInput: string
  activeLanguage: Language | null
  loading: boolean
  hasUserMessageSent: boolean
  chat: Message[]
  responseText: string
  audioQueue: string[]
  activeChannel: ChannelType
  waitIndex: number
  sessionReady: boolean
  showTerms: boolean
  sending: boolean
  termsFading: boolean
  isTranscribing: boolean
  pollingExpired: boolean
  lastPolled: number
}

const NEW_SESSION_STATE = {
  currentIndex: 0,
  chat: [],
  error: false,
  isMessageSending: false,
  isWaitingForAgent: false,
  aivaIsTyping: 0,
  isMuted: JSON.parse(localStorage.getItem(MUTED_STORAGE_KEY) ?? 'true'),
  responseText: '',
  audioQueue: [],
  activeChannel: 'CHAT' as ChannelType,
  waitIndex: 0,
  sending: false
} as { chat: Message[]; activeChannel: ChannelType }

/** ---------- Chat component ---------- */
class Chat extends Component<ChatProps, ChatState> {
  private transport: 'ws' | 'http' = 'ws'
  private keepAliveTimer: number | null = null
  private pollTimer: number | null = null
  private lastOptionsJSON: string | null = null
  private httpPollingExpiryAt: number | null = null

  private audioPlayer: HTMLAudioElement | null = null
  private footerRO?: ResizeObserver
  static contextType = LocalizationContext
  context!: React.ContextType<typeof LocalizationContext>

  conversationRef = createRef<HTMLDivElement>()
  bottomRef = createRef<HTMLDivElement>()
  // class field
  footerRef = createRef<HTMLDivElement>()

  // a11y helpers
  private politeLiveRef = createRef<HTMLDivElement>()
  private assertiveLiveRef = createRef<HTMLDivElement>()
  private previouslyFocused: HTMLElement | null = null
  private DIALOG_ID = 'ai-chat-dialog'
  private LOG_ID = 'ai-chat-log'
  private LABEL_ID = 'ai-chat-title'

  softKeyboard = false
  _clientY?: null | number = null

  CHAT_INPUT_MAX_HEIGHT = 150
  CHAT_INPUT_MIN_HEIGHT = 56

  input: HTMLTextAreaElement | null = null
  layout: HTMLDivElement | null = null

  submitButton: HTMLButtonElement | null = null
  _lastRequestId?: string

  wsClient: mqtt.AsyncMqttClient | null = null
  wsConfig: WSConfig | null = null

  state: ChatState = {
    ...NEW_SESSION_STATE,
    textInput: '',
    activeLanguage: null,
    loading: false,
    hasUserMessageSent: false,
    activeChannel: this.props.channel,
    sessionReady: false,
    showTerms: true,
    termsFading: false,
    isTranscribing: false,
    pollingExpired: false,
    lastPolled: Date.now()
  }

  private setImeInsetVar = () => {
    const vv = window.visualViewport
    let inset = 0
    if (vv) {
      // Amount of viewport lost to the keyboard
      inset = Math.max(0, window.innerHeight - vv.height)
      // Account for bottom offset (iOS sometimes shifts the viewport)
      inset = Math.max(0, inset - (vv.offsetTop || 0))
    }
    document.documentElement.style.setProperty(
      '--ime-inset',
      `${Math.round(inset)}px`
    )
  }

  private onFocusIn = (e: FocusEvent) => {
    const t = e.target as HTMLElement
    if (!t) return
    // limit to header buttons
    if (
      t.matches(
        '.btn-header, .header-menu .btn, .bottom .btn-submit, .bottom textarea'
      )
    ) {
      if (this.lastWasKeyboard) t.classList.add('kbd-focus')
    }
  }

  private onFocusOut = (e: FocusEvent) => {
    const t = e.target as HTMLElement
    if (
      t?.matches(
        '.btn-header, .header-menu .btn, .bottom .btn-submit, .bottom textarea'
      )
    ) {
      t.classList.remove('kbd-focus')
    }
  }

  private lastWasKeyboard = false
  private onKeyDown = (e: KeyboardEvent) => {
    // treat Tab / arrows / Enter / Space as keyboard navigation
    const keys = [
      'Tab',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Enter',
      ' '
    ]
    if (keys.includes(e.key)) this.lastWasKeyboard = true
  }
  private onMouseDown = () => {
    this.lastWasKeyboard = false
  }

  /** ---------- Lifecycle ---------- */
  componentDidMount() {
    // Bind global helpers AFTER instance methods are ready
    talkToChatHandler = (
      message: string,
      sender: Sender,
      type: MessageType,
      sendViaNetwork = true
    ) => this.talkToChat(message, sender, type, sendViaNetwork)

    sendMessageHandler = (
      message: string,
      type: MessageType,
      requestId?: string
    ) => this.sendMessage(message, type, requestId)

    const {
      publicKeyName,
      publicKeySecret,
      defaultHeaders,
      isChatWindowVisible,
      transcriptionDisabled
    } = this.props

    if (defaultHeaders) axios.defaults.headers.common = defaultHeaders
    if (publicKeyName && publicKeySecret) {
      const token = btoa(`${publicKeyName}:${publicKeySecret}`)
      axios.defaults.headers.Authorization = `Basic ${token}`
    }

    this.scrollChatToBottom()
    this.tryInitiateSession()

    document.addEventListener('keydown', this.onKeyDown, true)
    document.addEventListener('mousedown', this.onMouseDown, true)
    // (optional) treat touch as pointer/mouse
    document.addEventListener('pointerdown', this.onMouseDown, true)
    const root = this.footerRef.current
    if (root) {
      root.addEventListener('focusin', this.onFocusIn as any, true)
      root.addEventListener('focusout', this.onFocusOut as any, true)
    }

    if (isSmallDevice()) this.setScrollLock(this.props.isChatWindowVisible)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.viewportHandler)
      window.visualViewport.addEventListener('scroll', this.restoreBodyScroll)
      window.visualViewport.addEventListener('resize', this.setImeInsetVar)
      document.addEventListener('focusin', () => {}, { capture: true })
      this.viewportHandler()
      this.setImeInsetVar()
    }

    if (!transcriptionDisabled) {
      this.audioPlayer = new Audio()
      this.audioPlayer.preload = 'auto'

      // One-time gesture to unlock audio on iOS
      const unlockAudioOnce = () => {
        if (!this.audioPlayer) return
        this.audioPlayer.play().catch(() => {})
        this.audioPlayer.pause()
        this.audioPlayer.currentTime = 0
        this.retryQueuedAudio()
      }
      window.addEventListener(
        'click',
        () => {
          unlockAudioOnce()
          setTimeout(this.retryQueuedAudio, 0)
        },
        { once: true }
      )

      window.addEventListener(
        'AIVA_UNMUTE',
        () => this.changeAudioStatus(false),
        {
          once: true
        }
      )

      if (
        this.props.phoneTranscript &&
        this.props.recorderMode !== 'disabled'
      ) {
        this.setState({ activeChannel: 'VOICE' })
      }
    }

    // if (this.conversationRef.current) {
    //   const bottom = this.getComputedBottom(this.CHAT_INPUT_MIN_HEIGHT)
    //   // this.conversationRef.current.style.bottom = `${bottom}px`
    // }

    this.installFooterHeightVar()
  }

  componentDidUpdate(prevProps: ChatProps, prevState: ChatState) {
    const { chat, audioQueue } = this.state
    const { isChatWindowVisible } = this.props

    // Play queued audio if allowed
    if (
      !this.state.isMuted &&
      audioQueue.length &&
      this.audioPlayer &&
      this.audioPlayer.paused
    ) {
      const url = audioQueue[0]
      this.audioPlayer.src = url
      this.audioPlayer
        .play()
        .then(() => {
          this.audioPlayer!.onended = () =>
            this.setState((s) => ({ audioQueue: s.audioQueue.slice(1) }))
        })
        .catch((err) => {
          if (err.name !== 'NotAllowedError') {
            console.warn('[Chat] audio.play() failed', err)
            this.setState((s) => ({ audioQueue: s.audioQueue.slice(1) }))
          }
        })
    }

    if (
      prevProps.activeLanguage !== this.props.activeLanguage &&
      this.props.activeLanguage
    ) {
      const incoming: any = this.props.activeLanguage
      const enriched: any = {
        ...incoming,
        transcriptionUrl:
          incoming.transcriptionUrl ?? this.props.transcriptionUrl
      }
      if (
        !this.state.activeLanguage ||
        this.state.activeLanguage.locale !== enriched.locale ||
        // also update if we were missing extra fields
        (this.state.activeLanguage as any).transcriptionUrl !==
          enriched.transcriptionUrl
      ) {
        this.setState({ activeLanguage: enriched })
      }
    }

    // New messages → scroll down
    if (chat.length !== prevState.chat.length) this.scrollChatToBottom()

    const hasChatJustEnded =
      this.props.sessionId === null && !!prevProps.sessionId

    if (
      prevProps.sessionId === TEMP_SESSION_ID &&
      this.props.sessionId !== TEMP_SESSION_ID
    ) {
      // no-op
    } else {
      if (!hasChatJustEnded) this.tryInitiateSession(prevProps)
      else this.stopContinuesConnection()
    }

    // Popup visibility → lock/unlock body scroll on phones
    if (
      prevProps.isChatWindowVisible !== isChatWindowVisible &&
      isSmallDevice()
    ) {
      this.setScrollLock(isChatWindowVisible)
      this.viewportHandler()
    }

    // Expanded → focus dialog; Collapsed → stop audio + restore focus
    if (!prevProps.isExpanded && this.props.isExpanded) {
      this.previouslyFocused = (document.activeElement as HTMLElement) ?? null
      setTimeout(() => this.layout?.focus(), 0)
    }
    if (prevProps.isExpanded && !this.props.isExpanded) {
      this.audioPlayer?.pause()
      if (this.audioPlayer) this.audioPlayer.currentTime = 0
      this.setState({ audioQueue: [] })
      this.setMute(true)
      this.previouslyFocused?.focus()
      this.previouslyFocused = null
    }

    // Announce typing start/stop
    if (
      prevState.aivaIsTyping !== this.state.aivaIsTyping &&
      this.politeLiveRef.current
    ) {
      this.politeLiveRef.current.textContent =
        this.state.aivaIsTyping > 0
          ? `${this.props.botName || 'Assistent'} skriver…`
          : ''
    }

    // Widget got hidden → stop audio; visible again → try resume
    if (prevProps.isChatWindowVisible && !this.props.isChatWindowVisible) {
      this.haltAllAudio(false, false)
    } else if (
      !prevProps.isChatWindowVisible &&
      this.props.isChatWindowVisible
    ) {
      this.resumeAllAudio()
    }

    // Session ended → stop audio
    if (prevProps.sessionId && !this.props.sessionId) {
      this.haltAllAudio(false, true)
    }

    if (prevProps.isChatWindowVisible !== this.props.isChatWindowVisible) {
      const root = document.querySelector(
        '#aiva-chat .ChatContainer'
      ) as HTMLElement | null
      const launcher = document.querySelector(
        '#aiva-chat .chat-btn'
      ) as HTMLElement | null

      if (root) {
        if (!this.props.isChatWindowVisible) {
          root.setAttribute('inert', '')
          root.setAttribute('aria-hidden', 'true')
          launcher?.focus()
        } else {
          root.removeAttribute('inert')
          root.removeAttribute('aria-hidden')
          setTimeout(
            () => root.querySelector('.chatui-header .btn-header')?.focus(),
            0
          )
        }
      }
    }
  }

  componentWillUnmount() {
    this.stopContinuesConnection()
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.viewportHandler)
      window.visualViewport.removeEventListener('resize', this.setImeInsetVar)
      window.visualViewport.removeEventListener(
        'scroll',
        this.restoreBodyScroll
      )
    }

    document.removeEventListener('keydown', this.onKeyDown, true)
    document.removeEventListener('mousedown', this.onMouseDown, true)
    document.removeEventListener('pointerdown', this.onMouseDown, true)

    const root = this.footerRef.current
    if (root) {
      root.removeEventListener('focusin', this.onFocusIn as any, true)
      root.removeEventListener('focusout', this.onFocusOut as any, true)
    }

    window.removeEventListener('pointerdown', this.handleFirstGesture)
    this.footerRO?.disconnect()
  }

  private installFooterHeightVar = () => {
    const footer = this.footerRef.current
    if (!footer || typeof ResizeObserver === 'undefined') return

    const set = () => {
      const h = Math.ceil(footer.getBoundingClientRect().height)
      document.documentElement.style.setProperty('--footer-h', h + 'px')
    }

    this.footerRO = new ResizeObserver(set)
    this.footerRO.observe(footer)
    set()
  }

  /** ---------- Mobile/viewport & scroll helpers ---------- */
  private onContainerKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (
    e
  ) => {
    if (e.key === 'Escape' && this.props.isExpanded) {
      e.preventDefault()
      this.props.onToggleExpand?.()
    }
  }

  setMute = (muted: boolean) => {
    this.setState({ isMuted: muted }, () =>
      localStorage.setItem(MUTED_STORAGE_KEY, JSON.stringify(muted))
    )
  }

  setShowTerms = (show: boolean) => {
    if (!show) {
      this.setState({ termsFading: true }, () => {
        setTimeout(
          () => this.setState({ showTerms: false, termsFading: false }),
          150
        )
      })
      localStorage.setItem(INOVIA_TERMS_ACCEPTED, Date.now().toString())
    } else {
      this.setState({ showTerms: true })
    }
  }

  private ensureAudioUnlocked = () => {
    if (this.audioPlayer?.paused === false) return
    this.audioPlayer?.play().catch(() => {})
    this.audioPlayer?.pause()
    this.audioPlayer && (this.audioPlayer.currentTime = 0)
  }

  private haltAllAudio = (alsoMute = false, clearQueue = false) => {
    try {
      this.audioPlayer?.pause()
    } catch {}
    if (clearQueue) {
      if (this.audioPlayer) this.audioPlayer.currentTime = 0
      this.setState({ audioQueue: [], aivaIsTyping: 0 })
    }
    if (alsoMute) this.setMute(true)
  }

  private resumeAllAudio = () => {
    try {
      if (this.audioPlayer && this.state.audioQueue?.length) {
        if (this.audioPlayer.paused) this.audioPlayer.play()
      }
    } catch {}
  }

  addScrollListeners = () => {
    const passive = false
    window.addEventListener('touchmove', this.preventScroll, { passive })
    this.input?.addEventListener('touchmove', this.disableRubberBand)
    this.conversationRef.current?.addEventListener(
      'touchmove',
      this.disableRubberBandReverse
    )
    for (const element of [this.input, this.conversationRef.current])
      element?.addEventListener('touchstart', this.detectSingleTouch)
  }

  removeScrollListeners = () => {
    window.removeEventListener('touchmove', this.preventScroll)
    this.input?.removeEventListener('touchmove', this.disableRubberBand)
    this.conversationRef.current?.removeEventListener(
      'touchmove',
      this.disableRubberBandReverse
    )
    for (const element of [this.input, this.conversationRef.current])
      element?.removeEventListener('touchstart', this.detectSingleTouch)
  }

  tryInitiateSession = async (prevProps = {} as ChatProps) => {
    const { sessionId, isChatWindowVisible, onEvent } = this.props
    const { loading, chat } = this.state
    const didMount = !Object.keys(prevProps).length

    if (
      (!sessionId && isChatWindowVisible && !loading) ||
      (sessionId === TEMP_SESSION_ID && didMount && !chat?.length)
    ) {
      await this.loadCachedSession()
    } else if (
      sessionId &&
      !prevProps?.sessionId &&
      sessionId !== TEMP_SESSION_ID
    ) {
      try {
        await this.loadSession()
      } catch {
        await this.loadCachedSession()
      }
    } else if (didMount && onEvent)
      onEvent(EventType.LOADED, { aiva_active: true }, [])
  }

  viewportHandler = () => {
    const { isChatWindowVisible, isInbox } = this.props
    if (!this.layout) return
    if (!isInbox) {
      if (isChatWindowVisible && isSmallDevice()) {
        const heightFallback = this.softKeyboard ? '250px' : '100vh'
        const height = window.visualViewport
          ? `${window.visualViewport.height}px`
          : heightFallback
        if (
          this.layout.parentElement &&
          this.layout.parentElement.parentElement
        )
          this.layout.parentElement.parentElement.style.height = height
        this.layout.style.opacity = '1'
      } else {
        if (
          this.layout.parentElement &&
          this.layout.parentElement.parentElement
        )
          this.layout.parentElement.parentElement.style.height = ''
      }
    }
  }

  private _winY?: number

  setScrollLock = (lock?: boolean) => {
    if (lock) {
      this._winY = window.scrollY
      document.body.classList.add('lock-scroll')
      document.body.style.top = `-${this._winY}px`
      this.addScrollListeners()
    } else {
      document.body.classList.remove('lock-scroll')
      window.scrollTo(0, this._winY || 0)
      document.body.style.top = ''
      this.removeScrollListeners()
      this._winY = undefined
    }
  }

  preventScroll = (e: TouchEvent) => {
    const { target, touches } = e
    const isPinch = touches.length > 1
    const isValidScroll =
      this.input?.contains(target as Node) ||
      this.conversationRef.current?.contains(target as Node)
    if (!isValidScroll || isPinch) {
      blockEvent(e)
    }
  }

  restoreBodyScroll = () => {
    const { isChatWindowVisible } = this.props
    if (isChatWindowVisible && isSmallDevice()) {
      window.scrollTo(0, this._winY || 0)
    }
  }

  disableRubberBand = (event: TouchEvent) => {
    if (event.targetTouches.length !== 1) return
    const target = event.currentTarget as HTMLElement
    const clientY = event.targetTouches[0].clientY - (this._clientY || 0)
    const bounceTop = target?.scrollTop === 0 && clientY > 0
    const bounceBottom = isOverlayTotallyScrolled(target) && clientY < 0
    if (bounceTop || bounceBottom) event.preventDefault()
  }

  disableRubberBandReverse = (event: TouchEvent) => {
    if (event.targetTouches.length !== 1) return
    const target = event.currentTarget as HTMLElement
    const clientY = event.targetTouches[0].clientY - (this._clientY || 0)
    const bounceTop = isTotallyScrolledReversed(target) && clientY > 0
    const bounceBottom = target.scrollTop >= 0 && clientY < 0
    if (bounceTop || bounceBottom) event.preventDefault()
  }

  detectSingleTouch: EventListenerOrEventListenerObject = (event) => {
    const { targetTouches } = event as TouchEvent
    if (targetTouches.length === 1) {
      this._clientY = targetTouches[0].clientY
    }
  }

  /** ---------- Input & layout ---------- */
  onTextInputKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.which === 13 && !e.shiftKey) {
      e.preventDefault()
      if (!e.ctrlKey) this.submitButton?.click()
    }
  }

  updateFormSize = (formInput: HTMLTextAreaElement) => {
    if (!formInput.form) return
    formInput.form.style.height = `${this.CHAT_INPUT_MIN_HEIGHT}px`
    const height =
      formInput.scrollHeight <= this.CHAT_INPUT_MAX_HEIGHT
        ? formInput.scrollHeight
        : this.CHAT_INPUT_MAX_HEIGHT
    formInput.form.style.height = `${height}px`
    const inputHeight = Math.min(
      formInput.scrollHeight,
      this.CHAT_INPUT_MAX_HEIGHT
    )
    // const bottom = this.getComputedBottom(inputHeight)
    // if (this.conversationRef.current)
    //  this.conversationRef.current.style.bottom = `${bottom}px`
  }

  resizeConversationContainer = (e: HTMLTextAreaElement) => {
    const inputHeight = Math.min(e.scrollHeight, this.CHAT_INPUT_MAX_HEIGHT)
    // const bottom = this.getComputedBottom(inputHeight)
    // if (this.conversationRef.current)
    //   this.conversationRef.current.style.bottom = `${bottom}px`
    this.scrollChatToBottom()
  }

  private handleFirstGesture = () => {
    this.unlockAudio()
    this.retryQueuedAudio()
  }

  private retryQueuedAudio = () => {
    const { audioQueue } = this.state
    if (audioQueue.length > 0) {
      this.setState({ audioQueue: [...audioQueue] })
    }
  }

  unlockAudio = (transcriptionDisabled?: boolean) => {
    if (transcriptionDisabled) {
      return
    }

    const audio = new Audio('https://pages.inoviaai.se/scripts/silence.mp3')
    audio
      .play()
      .then(() => {
        audio.pause()
        audio.currentTime = 0
      })
      .catch(console.warn)
  }

  changeAudioStatus = (isMuted: boolean, transcriptionDisabled?: boolean) => {
    if (transcriptionDisabled) {
      return
    }

    if (isMuted === true) {
      this.audioPlayer?.pause()
      this.audioPlayer && (this.audioPlayer.currentTime = 0)
      this.setState({ audioQueue: [] })
    } else {
      this.ensureAudioUnlocked()
      // @ts-ignore
      window.AutomaticSpeechContext?.resume?.().catch?.(() => {})
    }
    this.setMute(isMuted)
  }

  onTextInputChange = (e: ChangeEvent) => {
    const target = e.target as HTMLTextAreaElement
    this.setTextInput(target.value)
    this.updateFormSize(target)
    this.resizeConversationContainer(target)
  }

  setTextInput = (text: string) => {
    this.setState({ textInput: text })
  }

  /** ---------- Sessions ---------- */
  newSession = async () => {
    this.stopContinuesConnection()
    this.setState({ ...NEW_SESSION_STATE, loading: true })
    return this.loadCachedSession()
  }

  loadCachedSession = async () => {
    const {
      channel,
      onEvent,
      setSessionId,
      conversationStartUrl,
      initialGreetingMessage,
      transcriptionDisabled
    } = this.props
    this.setState({ loading: true })
    await this.updateActiveLanguage()
    const { activeLanguage, isMuted } = this.state
    const { data } = await axios.post(conversationStartUrl, {
      locale: activeLanguage?.locale,
      channel
    })
    const { replies: messages, options, aiva_active } = data
    const id = TEMP_SESSION_ID
    setSessionId(id, () => {
      if (onEvent) onEvent(EventType.NEW_SESSION, { id }, [])
    })

    if (Array.isArray(messages) && messages.length > 0) {
      let currentIndex = Math.max(...messages.map((m: any) => m.index))
      try {
        if (options && options.length) {
          messages.push({
            type: MessageType.OPTIONS,
            index: currentIndex++,
            options
          })
        }
      } catch (e) {
        console.error(e)
      }

      if (!isMuted && !transcriptionDisabled) {
        messages
          .filter((m: any) => m.replyType === 'messageReply')
          .forEach((m: any) =>
            this.enqueueAudio(
              generateAudioUrl(
                m.message,
                activeLanguage?.locale || 'sv_SE',
                this.props.voices
              )
            )
          )
      }

      // store options block
      // @ts-ignore
      this.handleOptionUpdates({ options })

      // CHANGED: show first greeting (don’t filter it out)
      this.setState(
        {
          chat: messages, // ← previously filtered out the initialGreetingMessage
          currentIndex,
          loading: false,
          aivaIsTyping: 0,
          sending: false,
          sessionReady: true
        },
        () => {
          if (onEvent) onEvent(EventType.LOADED, { aiva_active }, messages)
        }
      )
      return true
    }
  }

  continueConversation = async () => {
    const { channel, setSessionId, sessionId, conversationContinueUrl } =
      this.props
    return new Promise(async (resolve) => {
      if (sessionId !== TEMP_SESSION_ID) {
        resolve(sessionId)
        return
      }
      const { activeLanguage, activeChannel } = this.state
      const { data } = await axios.post(conversationContinueUrl, {
        locale: activeLanguage?.locale,
        channel: activeChannel
      })
      const { id } = data
      setSessionId(id, async () => {
        await this.startContinuesConnection()
        resolve(id)
      })
    })
  }

  loadSession = async () => {
    const { channel, hasOtherInstances, initialGreetingMessage } = this.props
    const isTestChannel = channel === 'TEST'
    this.setState({ ...NEW_SESSION_STATE, loading: true })
    this.stopContinuesConnection()
    try {
      await this.startContinuesConnection()
      const foundMessages = await this.fetchOlderConversations()
      this.setState({ loading: false, sessionReady: true })
      if (!foundMessages && !isTestChannel && !hasOtherInstances)
        this.sendMessage(initialGreetingMessage || '', MessageType.TEXT).catch(
          this.showConnectionError
        )
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404)
        this.newSession()
    }
  }

  /** ---------- Form submit & scroll ---------- */
  onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    this.bumpHttpPollingDeadline()
    const textInput = this.state.textInput.trim()
    if (textInput.length === 0) return
    this.setTextInput('')
    this.talkToChat(textInput, this.props.senderTypes[0], MessageType.TEXT)
    if (this.input) {
      this.input.value = ''
      this.updateFormSize(this.input)
    }
  }

  scrollChatToBottom() {
    if (this.bottomRef.current) {
      requestAnimationFrame(() =>
        this.bottomRef.current!.scrollIntoView({
          block: 'end',
          behavior: 'smooth'
        })
      )
    } else if (this.conversationRef.current) {
      const el = this.conversationRef.current
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight
      })
    }
  }

  /** ---------- Chat I/O ---------- */
  talkToChat = (
    message: string,
    sender: Sender,
    type: MessageType,
    sendViaNetwork = true
  ) => {
    const { onMessage } = this.props
    const requestId = uuid()

    if (sender === Sender.AGENT && this.state.isWaitingForAgent) {
      this.setState((prevState) => ({ ...prevState, isWaitingForAgent: false }))
    }

    this.setState(
      (prevState) => {
        const newMessage: any = {
          index: prevState.currentIndex + 1,
          sender,
          message,
          timestamp: dayjs().valueOf() / 1000,
          type,
          requestId
        }
        onMessage?.([newMessage])
        return {
          chat: [...prevState.chat, newMessage],
          hasUserMessageSent: sendViaNetwork
        }
      },
      () => {
        if (sender === this.props.senderTypes[0]) {
          this.clearAllOptionsButtons()
          this._lastRequestId = requestId
          if (sendViaNetwork) this.sendMessage(message, type, requestId)
        }
      }
    )
  }

  clearAllOptionsButtons = () => {
    const { sessionId } = this.props
    this.setState(({ chat }) => ({
      chat: chat.filter((item) => item.type !== MessageType.OPTIONS)
    }))
    localStorage.removeItem(`${KEY_STORED_OPTIONS}_${sessionId}`)
  }

  httpSendMessage = async (
    message: string,
    type: MessageType,
    sessionId: string | null
  ) => {
    const { conversationsUrl } = this.props

    const payload = {
      type,
      message
    }

    await axios.post(`${conversationsUrl}/${sessionId}/messages`, payload)
    // No need to touch state here; your polling loop will pick up replies.
  }

  sendMessage = async (
    message: string,
    type: MessageType,
    requestId?: string
  ) => {
    const { isInbox, additionalReqBodyFields = {} } = this.props
    const { locale } = this.state.activeLanguage || {}

    try {
      this.setState((prevState) => ({
        aivaIsTyping: isInbox ? 0 : 1,
        isMessageSending: true,
        sending: true
      }))

      // Continues conversation will check if sessionId === TEMP_SESSION_ID
      const newSessionId = (await this.continueConversation()) as string

      if (this.transport === 'http') {
        await this.httpSendMessage(message, type, newSessionId)
        return
      } else {
        const messageBody = isInbox
          ? { session: newSessionId, message, locale }
          : { type, message }

        const messageReqBody = { ...messageBody, ...additionalReqBodyFields }
        this.wsPublish(
          {
            // @ts-ignore – backend expects the merged body
            message: messageReqBody,
            ...(requestId ? { requestId } : {})
          },
          newSessionId // ← ensure WS payload uses the real session id
        )
      }
    } catch (e) {
      const { chat: _chat, currentIndex } = this.state
      const failureMessage = _chat.findIndex((x) => x.index === currentIndex)
      if (failureMessage !== -1) {
        _chat[failureMessage].isFailed = true
        this.setState({ chat: _chat })
      }
      this.showConnectionError(e as Error)
    } finally {
      this.setState((prevState) => ({
        ...prevState,
        isMessageSending: false,
        sending: false
      }))
      this.input?.focus()
    }
  }

  showConnectionError = (e: Error) => {
    const { getLocalizedString } = this.context
    console.error(e)
    this.talkToChat(
      getLocalizedString(
        'connectionError',
        'Sorry. There was an error while connecting to server.'
      ),
      Sender.SYSTEM,
      MessageType.TEXT,
      false
    )
  }

  fetchOlderConversations = async () => {
    const { conversationsUrl, onEvent, sessionId, initialGreetingMessage } =
      this.props
    const {
      data: { locale, aiva_active }
    } = await axios.get(`${conversationsUrl}/${sessionId}`)
    this.updateActiveLanguage(locale)

    const url = `${conversationsUrl}/${sessionId}/messages`
    const {
      data: { messages }
    } = await axios.get(url)
    if (Array.isArray(messages) && messages.length > 0) {
      let currentIndex = Math.max(
        ...messages.map((message: any) => message.index)
      )
      try {
        const options = localStorage.getItem(
          `${KEY_STORED_OPTIONS}_${sessionId}`
        )
        if (options && options.length) {
          messages.push({
            type: MessageType.OPTIONS,
            index: currentIndex++,
            options: JSON.parse(options)
          })
        }
      } catch (e) {
        console.error(e)
      }

      // CHANGED: show first greeting from history
      this.setState(
        {
          chat: messages, // ← previously filtered out greeting here
          currentIndex
        },
        () => {
          onEvent?.(EventType.LOADED, { aiva_active }, messages)
        }
      )
      return true
    }
    onEvent?.(EventType.LOADED, { aiva_active }, [])
    return false
  }

  stopChat = async () => {
    const { setSessionId, onMessage } = this.props
    await this.wsPublish({
      messageType: WSPayloadType.STATUS_MESSAGE,
      connectionStatus: 'TERMINATED'
    })
    onMessage?.([])
    setSessionId(null, () => {
      this.setState({ sessionReady: false })
    })
    this.props.onMinimizeBtnClick?.()
  }

  switchLanguage = async (language: Language) => {
    if (language.locale === this.state.activeLanguage?.locale) return
    this.stopChat()
    await this.updateActiveLanguage(language.locale)
    this.props.onSwitchLocale?.(language.locale)
    this.newSession()
  }

  // isBadSafari =
  //   /iPhone|iPad|iPod/.test(navigator.userAgent) &&
  //   /Version\/26\./.test(navigator.userAgent) && // Safari 26
  //   /Safari/.test(navigator.userAgent) &&
  //   !/CriOS|FxiOS/.test(navigator.userAgent)

  // For testing
  isBadSafari = false

  private bumpHttpPollingDeadline = () => {
    this.httpPollingExpiryAt = Date.now() + 5 * 60 * 1000
  }

  private isHttpPollingExpired = () => {
    return (
      this.httpPollingExpiryAt !== null && Date.now() > this.httpPollingExpiryAt
    )
  }

  // --- REPLACE your startHttpPolling with this ---
  startHttpPolling = async (sessionId: string) => {
    const { conversationsUrl } = this.props
    this.stopHttpPolling()

    const poll = async () => {
      if (this.isHttpPollingExpired()) {
        this.setState({ pollingExpired: true, lastPolled: Date.now() })
        this.stopHttpPolling()
        return
      }

      this.setState({ lastPolled: Date.now() })

      try {
        const { data } = await axios.get(
          `${conversationsUrl}/${sessionId}/messages`
        )
        const incoming: any[] = Array.isArray(data?.messages)
          ? data.messages
          : []

        const sorted = [...incoming].sort(
          (a, b) => (a.index ?? 0) - (b.index ?? 0)
        )

        const newestSeen = this.state.currentIndex
        const freshReplies = sorted.filter(
          (m) =>
            typeof m.index === 'number' &&
            m.index > newestSeen &&
            m.type !== MessageType.OPTIONS
        )

        if (freshReplies.length) {
          this.handleMessageUpdates(freshReplies as any)
          this.setState({
            aivaIsTyping: 0,
            sending: false,
            hasUserMessageSent: false
          })
          this.bumpHttpPollingDeadline()
        }

        let incomingOptions: any[] | null = null

        if (Array.isArray(data?.options) && data.options.length) {
          incomingOptions = data.options
        }

        const embeddedOpts = sorted.find(
          (m) => m?.type === MessageType.OPTIONS && Array.isArray(m?.options)
        )
        if (!incomingOptions && embeddedOpts) {
          incomingOptions = embeddedOpts.options
        }

        if (incomingOptions && incomingOptions.length) {
          const json = JSON.stringify(incomingOptions)
          if (json !== this.lastOptionsJSON) {
            this.lastOptionsJSON = json
            this.handleOptionUpdates({ options: incomingOptions } as any)
            this.bumpHttpPollingDeadline()
          }
        }
      } catch {
        // silent retry
      }
    }

    await poll()
    this.pollTimer = window.setInterval(poll, 1500)
  }

  stopHttpPolling = () => {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
    this.setState({ pollingExpired: false })
  }

  /** ---------- WebSocket / MQTT ---------- */
  websocketProcess = async () => {
    try {
      if (this.isBadSafari) throw new Error('Force polling on iOS 26 Safari')

      const { websocketUrl } = this.props
      const { namespace } = this.wsConfig || {}
      const { sessionId, publicKeyName, publicKeySecret, isInbox } = this.props
      const sendUserType = isInbox ? 'agent' : 'user'
      const subscribePath = `${namespace}/${sendUserType}/subscribe/${sessionId}`

      let client = this.wsClient
      if (!client) {
        client = await mqtt.connectAsync(websocketUrl, {
          username: publicKeyName,
          password: publicKeySecret,
          protocolVersion: 4,
          clean: true,
          keepalive: 30,
          reconnectPeriod: 1000,
          connectTimeout: 10000
        })
        this.wsClient = client
      }

      await client.subscribe(subscribePath)

      if (!(client as any).__aivaHandlerInstalled) {
        client.on('message', async (_topic, message) => {
          const arrivedPayload = JSON.parse(message.toString())

          if (Array.isArray(arrivedPayload)) {
            arrivedPayload.forEach(this.onMessageUpdate)
          } else {
            this.onMessageUpdate(arrivedPayload)
          }

          if (arrivedPayload.messageType === PayloadMessageTypes.REPLY) {
            const { responseText } = this.state
            this.setState({
              responseText: `${responseText} ${arrivedPayload.message.message} `,
              aivaIsTyping: 0,
              sending: false
            })
            if (arrivedPayload.message.linkDisplayText) {
              this.setState((s) => ({
                responseText: `${s.responseText} ${arrivedPayload.message.linkDisplayText} `
              }))
            }
          }
        })
        ;(client as any).__aivaHandlerInstalled = true
      }
    } catch (err) {
      const id = await this.continueConversation()
      this.transport = 'http'
      this.startHttpPolling(id as string)
    }
  }

  private enqueueAudio = (url?: string | null) => {
    if (!url) return
    if (!this.audioPlayer) return
    if ((this.audioPlayer.canPlayType('audio/mpeg') ?? '') === '') return
    this.setState((s) => ({ audioQueue: [...s.audioQueue, url] }))
  }

  onMessageUpdate = (payload: PayloadMessage) => {
    const { isMuted, activeLanguage } = this.state
    const {transcriptionDisabled} = this.props;
    const locale = activeLanguage?.locale || 'sv_SE'

    if (payload.message && typeof payload.message.timestamp === 'string') {
      payload.message.timestamp =
        dayjs(payload.message.timestamp).valueOf() / 1000
    }

    if (payload.messageType === PayloadMessageTypes.REPLY) {
      this.handleMessageUpdates([payload.message])
      if (!isMuted && !transcriptionDisabled) {
        if (payload.message.message?.length) {
          this.enqueueAudio(
            generateAudioUrl(payload.message.message, locale, this.props.voices)
          )
        }
        if (payload.message.linkDisplayText?.length) {
          this.enqueueAudio(
            generateAudioUrl(
              payload.message.linkDisplayText,
              locale,
              this.props.voices
            )
          )
        }
      }
    }
    if (payload.messageType === PayloadMessageTypes.ASSIGNMENT) {
      this.handleStateUpdates(payload.message)
    }
    if (payload.messageType === PayloadMessageTypes.OPTIONS) {
      this.handleOptionUpdates(payload)
    }
    if (payload.messageType === PayloadMessageTypes.ERROR) {
      this.handleErrors(payload)
    }
    if (payload.messageType === PayloadMessageTypes.USER_MESSAGE) {
      this.handleUserMessage(payload.message, payload.requestId)
      if (this.state.chat?.length && !isMuted) {
        const waitUrl = generateWaitMessage(
          locale,
          this.props.voices,
          this.state.waitIndex
        )
        if (waitUrl) this.playAudio(waitUrl)
      }
    }
  }

  playAudio = (waitUrl: string) => {
    this.enqueueAudio(waitUrl)
    this.setState((s) => ({ waitIndex: s.waitIndex + 1 }))
  }

  delayAivaReply = (message: string) => {
    const { aivaTypingSpeed } = this.props
    const delay =
      (500 + (Math.min(message.length, 100) / config.charsPerSecond) * 1000) /
      (aivaTypingSpeed || 1)
    return new Promise((resolve) => setTimeout(resolve, delay))
  }

  wsPublish = async (
    payload: Partial<WSPublishPayload>,
    sessionIdOverride?: string
  ) => {
    if (!this.wsClient) return
    const { namespace } = this.wsConfig || {}
    const { isInbox } = this.props

    const sid = sessionIdOverride ?? this.props.sessionId
    const sendUserType = isInbox ? 'agent' : 'user'
    const messageContent = {
      messageType: `${sendUserType}Message` as WSPayloadType,
      sessionId: sid,
      ...payload
    }
    const destinationName = `${namespace}/${sendUserType}/publish`
    if (payload.messageType != WSPayloadType.KEEP_ALIVE) {
      this.setState({ responseText: '' })
    }
    await this.wsClient.publish(destinationName, JSON.stringify(messageContent))
  }

  sendKeepAlive = async () => {
    const { sessionId } = this.props
    if (!sessionId) return
    await this.wsPublish({
      messageType: WSPayloadType.KEEP_ALIVE,
      sessionId
    })
    await sleep(1000 * 5)
    this.sendKeepAlive()
  }

  startContinuesConnection = async () => {
    if (this.wsClient) return

    try {
      if (this.isBadSafari) throw new Error('Force HTTP fallback')

      const { data: wsConfig } = await axios.get<WSConfig>(this.props.configUrl)
      this.wsConfig = wsConfig

      const wsClient = await mqtt.connectAsync(this.props.websocketUrl, {
        username: this.props.publicKeyName,
        password: this.props.publicKeySecret,
        keepalive: 10,
        reconnectPeriod: 1000,
        connectTimeout: 100 * 1000,
        protocolVersion: 4,
        protocolId: 'MQTT',
        clean: true
      })

      this.transport = 'ws'
      this.wsClient = wsClient
      await this.websocketProcess()
      this.sendKeepAlive()
      this.stopHttpPolling?.()
    } catch (err) {
      this.transport = 'http'
      const id = await this.continueConversation()
      this.startHttpPolling?.(id as string)
    }
  }
  stopContinuesConnection = () => {
    if (this.wsClient) this.wsClient.end()
    this.wsClient = null
  }

  /** ---------- Message reducers ---------- */
  handleStateUpdates = (update: PayloadMessage['message']) => {
    const { aivaActive } = update
    const { customMessages = [], onEvent } = this.props
    const { chat } = this.state
    const messages = [...chat, ...customMessages].sort(
      (a, b) => a.index - b.index
    )
    onEvent?.(EventType.ASSIGNMENT, { aiva_active: aivaActive }, messages)
  }

  // CHANGED: don’t filter out the first greeting here either
  handleMessageUpdates = (updates: PayloadMessage['message'][]) => {
    const { onMessage } = this.props
    const { chat, currentIndex } = this.state

    // ignore anything we've already applied
    const fresh = updates.filter(
      (u) => typeof u.index === 'number' && u.index > currentIndex
    )

    const messages = fresh // ← previously filtered out initialGreetingMessage
    if (!messages.length) return

    const previousMessage = chat[chat.length - 1]
    const isErrorMessage =
      previousMessage && previousMessage.type === MessageType.ERROR

    if (messages.length && !isErrorMessage) {
      this.setState((prev) => ({
        chat: [...prev.chat, ...messages],
        currentIndex: messages[messages.length - 1].index
      }))
      this.clearAllOptionsButtons()
    }

    onMessage?.(messages)
  }

  handleOptionUpdates = (update: PayloadMessage) => {
    const { sessionId } = this.props
    this.setState((prevState) => ({
      chat: [
        ...prevState.chat,
        {
          type: MessageType.OPTIONS,
          index: prevState.currentIndex + 1,
          options: update.options
        }
      ],
      currentIndex: prevState.currentIndex + 1
    }))
    localStorage.setItem(
      `${KEY_STORED_OPTIONS}_${sessionId}`,
      JSON.stringify(update.options)
    )
  }

  handleErrors = ({ error, message, ...restProps }: PayloadMessage) => {
    const { channel } = this.props
    const errorText = channel === 'CHAT' ? 'An unknown error occurred' : error
    const payloadMessage =
      typeof message === 'string' ? message : (message as any).message
    this.setState((prevState) => ({
      chat: [
        ...prevState.chat,
        {
          type: MessageType.ERROR,
          index: prevState.currentIndex + 1,
          ...restProps,
          error: errorText,
          message: payloadMessage
        }
      ],
      currentIndex: prevState.currentIndex + 1
    }))
    if (this.assertiveLiveRef.current) {
      this.assertiveLiveRef.current.textContent = ''
      requestAnimationFrame(() => {
        this.assertiveLiveRef.current!.textContent =
          payloadMessage || 'Ett fel inträffade.'
      })
    }
  }

  handleUserMessage = (
    message: PayloadMessage['message'],
    requestId: string
  ) => {
    const { hasUserMessageSent, currentIndex } = this.state
    const { onEvent } = this.props
    onEvent?.(EventType.USER_MESSAGE, {}, [message])

    if (!hasUserMessageSent && currentIndex > 1) {
      this.talkToChat(
        message.message || '',
        Sender.CUSTOMER,
        message.type,
        false
      )
    } else {
      if (this._lastRequestId === requestId) {
        const chatIndex = this.state.chat.findIndex(
          (msg) => (msg as any).requestId === requestId
        )
        const chat = [...this.state.chat]
        ;(chat[chatIndex] as any).index = message.index
        this.setState({ chat, hasUserMessageSent: false })
      } else {
        this.setState({ hasUserMessageSent: false })
      }
    }
  }

  /** ---------- Language ---------- */
  /** ---------- Language ---------- */
  updateActiveLanguage = (value?: string) => {
    const {
      locale: propLocale,
      defaultLocale,
      languages,
      activeLanguage: activeLangProp,
      transcriptionUrl
    } = this.props

    // decide the locale we want
    let selectedLocale =
      value ??
      localStorage.getItem('chatui_selected_language') ??
      activeLangProp?.locale ??
      propLocale ??
      defaultLocale

    if (!selectedLocale) return

    // prefer a full object from languages[], else the activeLanguage prop, else a minimal fallback
    const fromList = languages?.find((l) => l.locale === selectedLocale)
    const base =
      fromList ??
      activeLangProp ??
      ({ locale: selectedLocale, label: '' } as any)

    // ensure required fields exist so children (AutoRecorder) always get them
    const enriched: any = {
      ...base,
      // make sure transcriptionUrl exists (fallback to Chat prop)
      transcriptionUrl: (base as any).transcriptionUrl ?? transcriptionUrl
    }

    // persist + notify i18n
    localStorage.setItem('chatui_selected_language', enriched.locale)
    this.context.setLocale(enriched.locale)

    // resolve after setState completes (callers await this)
    return new Promise((res) =>
      this.setState({ activeLanguage: enriched }, res as () => void)
    )
  }

  /** ---------- UI bits ---------- */
  private CallBar = () => (
    <div className="call-bar">
      <button
        onClick={() => this.changeAudioStatus(!this.state.isMuted)}
        title={this.state.isMuted ? 'Slå på ljud' : 'Stäng av ljud'}
        aria-pressed={!this.state.isMuted}
        aria-label={this.state.isMuted ? 'Slå på ljud' : 'Stäng av ljud'}
      >
        {this.state.isMuted ? <MuteIcon fill="black" /> : <SpeakerIcon />}
      </button>

      <button
        className="stop"
        onClick={() => {
          this.wsPublish({
            messageType: WSPayloadType.STATUS_MESSAGE,
            connectionStatus: 'TERMINATED'
          })
          this.props.onMinimizeBtnClick?.()
          localStorage.removeItem(KEY_CONVERSATION_ID)
        }}
        title="Avsluta samtal"
        aria-label="Avsluta samtal"
      >
        <CloseIcon />
      </button>

      <button
        onClick={() => this.props.onMinimizeBtnClick?.()}
        title="Dölj chattfönstret"
        aria-label="Dölj chattfönstret"
      >
        🗕
      </button>
    </div>
  )

  /** ---------- Computed helpers ---------- */
  getBaseBottomOffset = () => {
    let extra = 0
    // @ts-ignore - design stores footerText on activeLanguage
    if (this.state.activeLanguage?.footerText) extra += FOOTER_EXTRA_PX
    if (this.props.phoneTranscript) extra += CALLBAR_EXTRA_PX
    return extra
  }

  getComputedBottom = (inputHeight: number) =>
    inputHeight + this.getBaseBottomOffset()

  /** ---------- Render ---------- */
  render() {
    const { getLocalizedString } = this.context
    const {
      textInput,
      chat = [],
      isMessageSending,
      activeLanguage,
      isWaitingForAgent,
      aivaIsTyping,
      isMuted,
      loading
    } = this.state
    const {
      isInbox,
      icons,
      senderTypes,
      withAvatars,
      userAvatar,
      agentAvatar,
      customMessages = [],
      languages,
      isRenewSessionVisible,
      setSessionId,
      hideHeader,
      title,
      botName,
      voices,
      channel,
      transcriptionUrl,
      baseHost,
      isRecorderEnabled,
      freeText,
      isExpanded,
      onToggleExpand,
      transcriptionDisabled
    } = this.props

    let { readOnly } = this.props

    const updatedTranscriptionUrl = activeLanguage?.transcriptionUrl
      ? activeLanguage?.transcriptionUrl
      : transcriptionUrl

    const titleComponent =
      typeof title === 'object' && (title as any)?.src ? (
        <img
          className="chatTitle"
          src={(title as any).src}
          alt={(title as any).alt}
        />
      ) : (
        (title as string)
      )

    const options = chat.find((item) => item.type === MessageType.OPTIONS)
    readOnly = chat.find((item) => item.sender === 'SYSTEM') != null

    const chatHasMoreOptions =
      (options && (options.options?.length || 0) > 1) || false

    const recorderMode = this.props.recorderMode ?? 'manual'

    const srOnly: React.CSSProperties = {
      position: 'absolute',
      left: '-9999px',
      width: '1px',
      height: '1px',
      overflow: 'hidden'
    }

    return (
      <div
        className="chatui"
        ref={(el) => (this.layout = el)}
        id={this.DIALOG_ID}
        role={isExpanded ? 'dialog' : 'region'}
        aria-modal={isExpanded ? true : undefined}
        aria-labelledby={this.LABEL_ID}
        aria-controls={this.LOG_ID}
        tabIndex={-1}
        onKeyDown={this.onContainerKeyDown}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          paddingBottom: 'env(safe-area-inset-bottom)',
          background: '#fff'
        }}
      >
        {!hideHeader && (
          <Header
            printTargetRef={this.conversationRef}
            languageSwitchProps={{
              isSwitchVisible: !!(
                languages &&
                languages.length > 1 &&
                activeLanguage
              ),
              onSwitchLanguage: this.switchLanguage,
              languages: languages,
              activeLanguage: activeLanguage
            }}
            newSessionBtnProps={{
              isNSBVisible: !isInbox && isRenewSessionVisible,
              onClick: this.newSession,
              disabled: isMessageSending || !activeLanguage
            }}
            onMinimizeBtnClick={this.props.onMinimizeBtnClick}
            setSessionId={setSessionId}
            wsPublish={this.wsPublish}
            channel={channel}
            isMuted={isMuted}
            changeAudioStatus={this.changeAudioStatus}
            unlockAudio={this.ensureAudioUnlocked}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
            stopChat={this.stopChat}
            about={{}}
            transcriptionDisabled={this.props.transcriptionDisabled}
          >
            <span id={this.LABEL_ID}>{titleComponent}</span>
          </Header>
        )}

        {/* Skip link for keyboard users */}
        <a href={`#${this.LOG_ID}`} style={srOnly}>
          Hoppa till chatten
        </a>

        {/* Live regions for screen readers */}
        <div
          ref={this.politeLiveRef}
          aria-live="polite"
          role="status"
          style={srOnly}
        />
        <div
          ref={this.assertiveLiveRef}
          aria-live="assertive"
          role="alert"
          style={srOnly}
        />

        <div
          className="conversation chatui-container"
          ref={this.conversationRef}
          id={this.LOG_ID}
          role="log"
          aria-live="polite"
          aria-relevant="additions text"
          aria-label="Chattmeddelanden"
          style={{
            order: 1,
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            paddingBottom: 'var(--footer-h, 56px)'
          }}
        >
          <RenderMessages
            chat={[...chat, ...customMessages].sort(
              (a, b) => a.index - b.index
            )}
            talkToChat={this.talkToChat}
            senderTypes={senderTypes}
            withAvatars={withAvatars}
            userAvatar={userAvatar}
            agentAvatar={agentAvatar}
            isWaitingForAgent={isWaitingForAgent}
            botName={botName}
            channel={channel}
            sessionId={this.props.sessionId}
            baseHost={baseHost}
            isInbox={isInbox}
          />
          {loading && <LoadingSpinner />}
          <div ref={this.bottomRef} />
        </div>

        {aivaIsTyping > 0 && (
          <div
            className="typing-indicator"
            aria-live="polite"
            aria-atomic="true"
            style={{
              order: 2,
              position: 'relative',
              margin: '0.25rem 0',
              padding: '0 0.75rem',
              zIndex: 2
            }}
          >
            {getLocalizedString('aivaIsTyping', 'Is typing')}
            <span className="dot dot-1"></span>
            <span className="dot dot-2"></span>
            <span className="dot dot-3"></span>
          </div>
        )}

        {!readOnly && (
          <div
            ref={this.footerRef}
            className={`bottom chatui-container ${
              chatHasMoreOptions && !freeText ? '' : 'open'
            }`}
            style={{
              order: 3,
              position: 'relative',
              flex: '0 0 auto',
              background: '#fff',
              paddingBottom: 'env(safe-area-inset-bottom)'
            }}
          >
            <form
              className="textinput"
              onClick={() => this.input && this.input.focus()}
              onSubmit={this.onSubmit}
              style={{ height: `${this.CHAT_INPUT_MIN_HEIGHT}px` }}
            >
              <textarea
                value={textInput}
                autoComplete="off"
                name="textInput"
                aria-label="Skriv meddelande"
                aria-describedby={
                  activeLanguage?.footerText ? 'chat-footer-desc' : undefined
                }
                onChange={this.onTextInputChange}
                placeholder={
                  isMessageSending
                    ? getLocalizedString('sending', 'Sending...')
                    : getLocalizedString('typeHere', 'Type')
                }
                disabled={isMessageSending}
                ref={(el) => (this.input = el)}
                onKeyDown={this.onTextInputKeyDown}
              />
              <div className="btn-group">
                {textInput.length == 0 && isRecorderEnabled === true ? (
                  recorderMode === 'manual' ? (
                    <Recorder
                      talkToChat={this.talkToChat}
                      transcriptionUrl={updatedTranscriptionUrl}
                      activeLanguage={activeLanguage || null}
                      isInbox={isInbox}
                      setMessageSending={(bool) =>
                        this.setState({
                          isMessageSending: bool,
                          activeChannel: 'VOICE'
                        })
                      }
                      ref={(el) => (this.submitButton = el)}
                    />
                  ) : (
                    <AutoRecorder
                      key={this.props.sessionId || undefined}
                      activeLanguage={activeLanguage || null}
                      transcriptionUrl={updatedTranscriptionUrl}
                      talkToChat={this.talkToChat}
                      isBotBusy={isMessageSending || aivaIsTyping > 0}
                      stopSignal={chat.some(
                        (m) =>
                          m.sender === Sender.AGENT &&
                          /conversation ended|dialogen avslutad/i.test(
                            (m as any).message ?? ''
                          )
                      )}
                      onStopped={() => {}}
                      voices={this.props.voices}
                      playAudio={this.playAudio}
                      aivaIsTyping={aivaIsTyping}
                      isTranscribing={this.state.isTranscribing}
                      onTranscriptionStatusChange={(busy) => {
                        this.setState({ isTranscribing: busy })
                      }}
                      onRecordingStatusChange={(isRecording) => {
                        if (isRecording) {
                          this.audioPlayer?.pause()
                        } else {
                          if (
                            this.audioPlayer?.paused &&
                            this.state.audioQueue?.length
                          ) {
                            this.audioPlayer.play().catch(() => {})
                          }
                        }
                      }}
                      conversationId={this.props.sessionId || ''}
                    />
                  )
                ) : (
                  <button
                    className="btn btn-submit"
                    type="submit"
                    disabled={isMessageSending}
                    aria-label={getLocalizedString(
                      'sendMessage',
                      'Skicka meddelande'
                    )}
                    title={getLocalizedString(
                      'sendMessage',
                      'Skicka meddelande'
                    )}
                    ref={(el) => (this.submitButton = el)}
                  >
                    {icons && icons.sendIcon ? (
                      <img
                        className="chatIcon"
                        src={icons.sendIcon}
                        alt={getLocalizedString('sendIcon', 'Skicka')}
                      />
                    ) : (
                      <SendIcon className="chatIcon" />
                    )}
                  </button>
                )}
              </div>
            </form>

            {(this.isBadSafari || activeLanguage?.footerText) && (
              <div className="ChatFooter" id="chat-footer-desc">
                {`${activeLanguage?.footerText} ${
                  this.isBadSafari
                    ? this.isHttpPollingExpired()
                      ? 'Chatten behöver uppdateras'
                      : this.httpPollingExpiryAt
                      ? `Inaktiv om ${(
                          (this.httpPollingExpiryAt - Date.now()) /
                          1000
                        ).toFixed(0)}s`
                      : ''
                    : ''
                }`}
              </div>
            )}

            {this.props.phoneTranscript && <this.CallBar />}

            {this.props.phoneTranscript && this.state.sending && (
              <div
                className="sending-overlay"
                aria-live="polite"
                aria-atomic="true"
              >
                Skickar<span id="count">3</span>…
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
}

export default Chat
